// Build-time seed for the index pages (/[lang]/events, /[lang]/tours,
// /[lang]/articles, /[lang]/boats, the city landing pages and their
// /[city]/events + /[city]/tours pages, /[lang]/wine-tours and
// /[lang]/boat-rental/[city] + /[lang]/boat-rental/[city]/[type]).
//
// Those pages used to prerender nothing but a "Loading…" string: their rows
// arrive from Supabase/Sanity in an effect, and effects don't run during the
// static export. The result was an index page with no content and — because
// the cards ARE the site's only internal links — a crawl graph with no edges
// at all.
//
// This module fetches all three lists once per export process and parks them
// in the same static-prefetch cache the detail pages use, so the pages can
// render their cards synchronously during the build render. Each page then
// bakes its rows into its HTML (<ListSeed>), and useEvents/useTours/useArticles
// read that back on the client's first render so hydration matches.
//
// Called from app/[lang]/_layout.tsx's generateStaticParams — the one hook that
// is guaranteed to run, and to finish, before any page in the subtree renders.

import {
  BOATS_INDEX_QUERY,
  BOATS_INDEX_SEED_KEY,
  BOAT_RENTAL_CITIES_SEED_KEY,
  BOAT_TYPE_PAGES_SEED_KEY,
  CITY_EVENT_CATEGORY_PAGES_SEED_KEY,
  CITY_LISTING_PAGES_SEED_KEY,
  CROSS_SELL_LIMIT,
  EVENT_CATEGORIES_SEED_KEY,
  EVENTS_INDEX_QUERY,
  EVENTS_INDEX_SEED_KEY,
  EVENTS_RPC,
  FOOTER_LINKS_SEED_KEY,
  LIST_BUCKET,
  TOURS_INDEX_QUERY,
  TOURS_INDEX_SEED_KEY,
  WINE_THEME,
  WINE_TOURS_INDEX_QUERY,
  WINE_TOURS_INDEX_SEED_KEY,
  WINE_TOUR_CITIES_SEED_KEY,
  articlesIndexSeedKey,
  boatRentalCitySeedKey,
  boatRentalStatsSeedKey,
  boatTypePageSeedKey,
  boatTypeStatsSeedKey,
  cityBoatsQuery,
  cityBoatsSeedKey,
  cityEventsPageQuery,
  cityEventSliceSeedKey,
  cityEventsPageSeedKey,
  cityEventsQuery,
  cityEventsSeedKey,
  cityToursPageQuery,
  cityToursPageSeedKey,
  cityToursQuery,
  cityToursSeedKey,
  crossSellBoatsSeedKey,
  crossSellToursSeedKey,
  eventsRpcArgs,
  toursRpcArgs,
  wineToursCityQuery,
  wineToursCitySeedKey,
  type FooterCityLinks,
} from '@todo/core/list-queries';
import {
  TOP_BOATS_ON_CITY_PAGE,
  boatCityStats,
  boatRentalCityIds,
  boatTypePages,
  boatsOfTypePage,
} from '@/lib/boat-rental';
import { isBoatDelisted, type Boat } from '@todo/core/boats';
import {
  EVENT_WINDOW_SLICES,
  NEXT_EVENTS_ON_EMPTY_WINDOW,
  TOP_EVENTS_ON_SLICE_PAGE,
  WINDOW_DATE_ID,
  cityEventCategoryIds,
  type CityEventCategoryPages,
  type EventSliceSeed,
} from '@/lib/city-event-slices';
import {
  TOP_EVENTS_ON_CITY_PAGE,
  cityEventsPageIds,
  cityToursPageIds,
  type CityListingPages,
} from '@todo/core/city-listings';
import { fetchAllBoats } from '@todo/core/detail/boat-detail';
import { DEFAULT_EVENT_FILTERS, eventFilterArgs } from '@todo/core/hub-filters';
import { categories, cities } from '@/lib/events-data';
import { SUPPORTED_LOCALES, type Locale } from '@todo/core/i18n/locales';
import { sanityClient } from '@todo/core/sanity/client';
import { articlesQuery, type Article } from '@todo/core/sanity/queries';
import { getPrefetched, setPrefetched } from '@todo/core/static-prefetch';
import type { LocaleCopy } from '@todo/core/supabase';
import { supabase, type DbEvent } from '@todo/core/supabase';
import { tourIsPrivate, type Tour } from '@todo/core/tours';
import { wineTourCityIds } from '@/lib/wine-tours';

/**
 * The seed is baked verbatim into each hub page's HTML, so every byte in it is
 * shipped to every visitor. The full rows carry a long `description` plus three
 * locales x {title, description, summary} of translations — none of which the
 * card components read. Baking them whole put 685KB of JSON into
 * /en/tours (65% of a 1.1MB page) and cost ~16 Lighthouse points.
 *
 * These trimmers keep exactly the fields EventCard / TourCard and the pages'
 * ItemList JSON-LD actually render. IMPORTANT: the hooks no longer refetch a
 * seeded list (see use-tours.ts), so a field dropped here is simply absent on
 * the hub page — if a card starts rendering a new field, add it here too.
 */
function trimCopy(copy: LocaleCopy | undefined): LocaleCopy | undefined {
  // Only the title is rendered in a list; description/summary are detail-page
  // concerns and are by far the bulk of the payload.
  return copy ? { title: copy.title, description: '', summary: '' } : undefined;
}

function trimTranslations<T extends { en?: LocaleCopy; fr?: LocaleCopy; ru?: LocaleCopy }>(
  t: T | null | undefined,
): T | null {
  if (!t) return null;
  return { en: trimCopy(t.en), fr: trimCopy(t.fr), ru: trimCopy(t.ru) } as T;
}

/** Fields EventCard renders, plus what events ItemList needs. */
function trimEvent(e: DbEvent): DbEvent {
  return {
    id: e.id,
    source: e.source,
    source_id: '',
    title: e.title,
    description: null,
    category_id: e.category_id,
    city_id: e.city_id,
    start_ts: e.start_ts,
    end_ts: e.end_ts,
    tz: e.tz,
    venue_name: e.venue_name,
    performer: null,
    address: null,
    image_url: e.image_url,
    ticket_url: null,
    min_price: e.min_price,
    currency: e.currency,
    translations: trimTranslations(e.translations),
    show_slug: e.show_slug,
    ...(e.date_count != null ? { date_count: e.date_count } : {}),
  };
}

/** Fields TourCard renders, plus what tours ItemList needs. */
function trimTour(t: Tour): Tour {
  return {
    id: t.id,
    source: t.source,
    source_id: '',
    title: t.title,
    description: null,
    city_id: t.city_id,
    rating: t.rating,
    review_count: t.review_count,
    duration: t.duration,
    from_price: t.from_price,
    currency: t.currency,
    image_url: t.image_url,
    booking_url: null,
    delisted_at: null, // lists only ever hold listed tours (tours_by_city / tours_by_theme)
    translations: trimTranslations(t.translations),
  };
}

/**
 * The /[city]/tours and /wine-tours seeds: the cards, plus the PRIVATE_TOUR
 * flag the city page's filter and the wine pages' private block / comparison
 * table need — read from `raw` here, which no seed carries.
 */
function trimTourForCityPage(t: Tour): Tour {
  return { ...trimTour(t), is_private: tourIsPrivate(t) };
}

/** Fields BoatCard renders, plus what the boat ItemLists and city-page list need. */
function trimBoat(b: Boat): Boat {
  return {
    id: b.id,
    source: b.source,
    source_id: '',
    name: b.name,
    boat_type: b.boat_type,
    city_id: b.city_id,
    rating: b.rating,
    year: null,
    owner_type: null,
    from_price: b.from_price,
    currency: b.currency,
    image_url: b.image_url,
    booking_url: null,
    has_owner_text: b.has_owner_text,
    delisted_at: null,
    translations: b.translations
      ? { ...trimTranslations(b.translations), _title_suffix: b.translations._title_suffix }
      : null,
  };
}

/**
 * A /boat-rental/[city] page bakes its whole city (up to ~260 boats), and past
 * the top-rated card grid each boat is only a text link — so its seed keeps
 * just the page's own locale's title, and an image only for the card rows.
 * Halves the Cannes page's seed versus trimBoat.
 */
function trimBoatForRentalPage(b: Boat, index: number, locale: Locale): Boat {
  const copy = b.translations?.[locale];
  return {
    ...trimBoat(b),
    image_url: index < TOP_BOATS_ON_CITY_PAGE ? b.image_url : null,
    translations: copy
      ? { [locale]: trimCopy(copy), _title_suffix: b.translations?._title_suffix }
      : null,
  };
}

/**
 * A /[city]/events page bakes the city's whole event list (~200 rows in Nice),
 * and past the top cards each event is only a text link — so, like
 * trimBoatForRentalPage, its seed keeps just the page's own locale's title and
 * an image only for the card rows.
 */
function trimEventForCityPage(
  e: DbEvent,
  index: number,
  locale: Locale,
  cards = TOP_EVENTS_ON_CITY_PAGE,
): DbEvent {
  const copy = e.translations?.[locale];
  return {
    ...trimEvent(e),
    image_url: index < cards ? e.image_url : null,
    translations: copy ? { [locale]: trimCopy(copy) } : null,
  };
}

// Events and tours don't vary by locale (the translations ride along inside
// each row), so those two run once even though generateStaticParams is called
// per locale. Articles are separate Sanity documents per locale, so that fetch
// runs once per supported locale instead.
let listsPromise: Promise<void> | null = null;

// `articlesQuery` already selects only the fields ArticleCard, the hub's
// ItemList JSON-LD and the article pages' "More articles" ranking need (no
// `body`), so unlike events/tours there's no trim step here.
async function fetchArticlesIndexLists(): Promise<void> {
  await Promise.all(
    SUPPORTED_LOCALES.map(async (locale) => {
      try {
        const articles = await sanityClient.fetch<Article[]>(articlesQuery, { lang: locale });
        setPrefetched(LIST_BUCKET, articlesIndexSeedKey(locale), articles ?? []);
      } catch (err) {
        console.warn(
          `list-prefetch: articles seed failed for ${locale} — ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    }),
  );
}

/**
 * One city landing page's events + tours sections. Same reasoning as the
 * unfiltered hub seed above — the cards are the whole point of the page, so
 * they need to be real links in the static HTML, not something that only
 * shows up after hydration. Events/tours don't vary by locale, so — like the
 * unfiltered lists — this runs once and is shared across all three locales'
 * pages for the same city.
 */
async function fetchCityLists(): Promise<void> {
  await Promise.all(
    cities.map(async (city) => {
      const [events, tours] = await Promise.all([
        Promise.resolve(supabase.rpc(EVENTS_RPC, eventsRpcArgs(cityEventsQuery(city.id)))),
        Promise.resolve(supabase.rpc('tours_by_city', toursRpcArgs(cityToursQuery(city.id)))),
      ]);

      if (events.error) {
        console.warn(`list-prefetch: city events seed failed for ${city.id} — ${events.error.message}`);
      } else {
        setPrefetched(
          LIST_BUCKET,
          cityEventsSeedKey(city.id),
          ((events.data ?? []) as DbEvent[]).map(trimEvent),
        );
      }

      if (tours.error) {
        console.warn(`list-prefetch: city tours seed failed for ${city.id} — ${tours.error.message}`);
      } else {
        setPrefetched(
          LIST_BUCKET,
          cityToursSeedKey(city.id),
          ((tours.data ?? []) as Tour[]).map(trimTour),
        );
      }
    }),
  );
}

/**
 * The /[city]/events and /[city]/tours pages: each city's full lists, plus the
 * list of cities that clear the MIN_*_PER_CITY_PAGE floor (read back by the
 * pages' generateStaticParams and baked wherever something links to them). A
 * failed fetch leaves that city out of the page list rather than exporting an
 * empty page.
 */
async function fetchCityPageLists(): Promise<void> {
  const eventRows: DbEvent[] = [];
  const tourRows: Tour[] = [];

  await Promise.all(
    cities.map(async (city) => {
      const [events, tours] = await Promise.all([
        Promise.resolve(supabase.rpc(EVENTS_RPC, eventsRpcArgs(cityEventsPageQuery(city.id)))),
        Promise.resolve(supabase.rpc('tours_by_city', toursRpcArgs(cityToursPageQuery(city.id)))),
      ]);

      if (events.error) {
        console.warn(`list-prefetch: city events page seed failed for ${city.id} — ${events.error.message}`);
      } else {
        const rows = (events.data ?? []) as DbEvent[];
        eventRows.push(...rows);
        for (const locale of SUPPORTED_LOCALES) {
          setPrefetched(
            LIST_BUCKET,
            cityEventsPageSeedKey(city.id, locale),
            rows.map((event, index) => trimEventForCityPage(event, index, locale)),
          );
        }
      }

      if (tours.error) {
        console.warn(`list-prefetch: city tours page seed failed for ${city.id} — ${tours.error.message}`);
      } else {
        const rows = (tours.data ?? []) as Tour[];
        tourRows.push(...rows);
        setPrefetched(LIST_BUCKET, cityToursPageSeedKey(city.id), rows.map(trimTourForCityPage));
      }
    }),
  );

  const pages: CityListingPages = {
    events: cityEventsPageIds(eventRows),
    tours: cityToursPageIds(tourRows),
  };
  setPrefetched(LIST_BUCKET, CITY_LISTING_PAGES_SEED_KEY, pages);
  await fetchCityEventSlices(pages.events, eventRows);

  const present = new Set(eventRows.map((event) => event.category_id ?? 'other'));
  setPrefetched(
    LIST_BUCKET,
    EVENT_CATEGORIES_SEED_KEY,
    categories.map((category) => category.id).filter((id) => present.has(id)),
  );
}

/**
 * The /[city]/events/[slice] pages of every city with a /[city]/events page:
 * its date windows (today / this weekend / this week, computed now — the daily
 * build is what keeps them current) and its categories above
 * MIN_EVENTS_PER_CATEGORY_PAGE. A window comes from event_shows_filtered, so a
 * recurring show that plays on Saturday is in "this weekend" even when its
 * soonest date is today (the city list only carries each show's soonest
 * date); a category is a slice of the city list. A failed window fetch bakes
 * nothing, and the page then fetches its window in the browser.
 */
async function fetchCityEventSlices(cityIds: string[], eventRows: DbEvent[]): Promise<void> {
  const builtAt = new Date().toISOString();
  const categoryPages: CityEventCategoryPages = {};
  const bake = (cityId: string, slice: string, seed: Omit<EventSliceSeed, 'builtAt'>) => {
    for (const locale of SUPPORTED_LOCALES) {
      const trim = (rows: DbEvent[]) =>
        rows.map((event, index) => trimEventForCityPage(event, index, locale, TOP_EVENTS_ON_SLICE_PAGE));
      setPrefetched(LIST_BUCKET, cityEventSliceSeedKey(cityId, slice, locale), {
        ...seed,
        rows: trim(seed.rows),
        ...(seed.next ? { next: trim(seed.next) } : {}),
        builtAt,
      } satisfies EventSliceSeed);
    }
  };

  await Promise.all(
    cityIds.map(async (cityId) => {
      // RPC order: soonest first, then id — the order the city list has too.
      const inCity = eventRows.filter((event) => event.city_id === cityId);

      const ids = cityEventCategoryIds(inCity);
      categoryPages[cityId] = ids;
      for (const id of ids) {
        bake(cityId, id, {
          rows: inCity.filter((event) => event.category_id === id),
          from: null,
          until: null,
        });
      }

      await Promise.all(
        EVENT_WINDOW_SLICES.map(async (slice) => {
          const args = eventFilterArgs({ ...DEFAULT_EVENT_FILTERS, when: WINDOW_DATE_ID[slice] }, cityId, 730);
          const { data, error } = await supabase.rpc('event_shows_filtered', args);
          if (error) {
            console.warn(`list-prefetch: ${cityId}/events/${slice} seed failed — ${error.message}`);
            return;
          }
          const rows = (data ?? []) as DbEvent[];
          bake(cityId, slice, {
            rows,
            from: args.p_from,
            until: args.p_until,
            ...(rows.length === 0 ? { next: inCity.slice(0, NEXT_EVENTS_ON_EMPTY_WINDOW) } : {}),
          });
        }),
      );
    }),
  );

  // In `cities` order, like every other page list.
  setPrefetched(
    LIST_BUCKET,
    CITY_EVENT_CATEGORY_PAGES_SEED_KEY,
    Object.fromEntries(cityIds.map((id) => [id, categoryPages[id] ?? []])),
  );
}

/**
 * The /wine-tours hub + per-city pages. One fetch of every wine tour (the
 * whole theme is a few hundred rows at most) feeds all three seeds: the hub's
 * list, each city page's list, and the list of cities that get a page at all
 * (read back by generateWineCityStaticParams). Slicing one ordered result
 * rather than issuing a query per page keeps those three consistent with each
 * other — a city link on the hub always points at an exported page.
 */
async function fetchWineLists(): Promise<void> {
  const { data, error } = await supabase.rpc(
    'tours_by_theme',
    toursRpcArgs({ theme: WINE_THEME, limit: 1000 }),
  );
  if (error) {
    // No seed → the hub renders client-side only and no city pages are
    // exported (generateWineCityStaticParams sees an empty list).
    console.warn(`list-prefetch: wine tours seed failed — ${error.message}`);
    return;
  }

  const all = (data ?? []) as Tour[];
  const cityIds = wineTourCityIds(all);
  setPrefetched(LIST_BUCKET, WINE_TOUR_CITIES_SEED_KEY, cityIds);
  setPrefetched(
    LIST_BUCKET,
    WINE_TOURS_INDEX_SEED_KEY,
    all.slice(0, WINE_TOURS_INDEX_QUERY.limit).map(trimTourForCityPage),
  );
  for (const cityId of cityIds) {
    setPrefetched(
      LIST_BUCKET,
      wineToursCitySeedKey(cityId),
      all
        .filter((tour) => tour.city_id === cityId)
        .slice(0, wineToursCityQuery(cityId).limit)
        .map(trimTourForCityPage),
    );
  }
}

/**
 * Every boat seed from one fetch of the whole catalogue (~800 rows — the same
 * memoized fetch generateBoatStaticParams uses): the /boats hub + home
 * carousel, each city's top slice (city landing page section and the boat
 * detail pages' "more boats" block), and the /boat-rental/[city] pages' full
 * lists, stats and page list, and the same for the /boat-rental/[city]/[type]
 * pages. Ordered like the boats_by_city RPC (indexable boats first, then
 * rating desc nulls last, then id) so a seeded list matches what the client
 * query would return.
 */
async function fetchBoatLists(): Promise<void> {
  let rows: Boat[];
  try {
    rows = await fetchAllBoats();
  } catch (err) {
    // No seed → the boat lists render client-side only and no /boat-rental
    // pages are exported (generateBoatRentalCityStaticParams sees no cities).
    console.warn(`list-prefetch: boats seed failed — ${err instanceof Error ? err.message : String(err)}`);
    return;
  }

  // Listed boats only, like boats_by_city — a delisted boat keeps its detail
  // page (generateBoatStaticParams) but no list links to it. Same total order
  // as boats_by_city (has_owner_text first — the noindexed boilerplate boats
  // shouldn't take the card slots — then rating desc nulls last, then id): the
  // /boats hub pages past this seed with an offset into that RPC, so the two
  // must agree on how tied ratings are ordered. Lowercase-hex string compare
  // matches Postgres' uuid order.
  const all = rows.filter((boat) => !isBoatDelisted(boat)).sort(
    (a, b) =>
      Number(b.has_owner_text) - Number(a.has_owner_text) ||
      (b.rating ?? -1) - (a.rating ?? -1) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
  setPrefetched(
    LIST_BUCKET,
    BOATS_INDEX_SEED_KEY,
    all.slice(0, BOATS_INDEX_QUERY.limit).map(trimBoat),
  );

  const rentalCityIds = boatRentalCityIds(all);
  setPrefetched(LIST_BUCKET, BOAT_RENTAL_CITIES_SEED_KEY, rentalCityIds);

  for (const city of cities) {
    const inCity = all.filter((boat) => boat.city_id === city.id);
    setPrefetched(
      LIST_BUCKET,
      cityBoatsSeedKey(city.id),
      inCity.slice(0, cityBoatsQuery(city.id).limit).map(trimBoat),
    );
    if (rentalCityIds.includes(city.id)) {
      for (const locale of SUPPORTED_LOCALES) {
        setPrefetched(
          LIST_BUCKET,
          boatRentalCitySeedKey(city.id, locale),
          inCity.map((boat, index) => trimBoatForRentalPage(boat, index, locale)),
        );
      }
      // From the untrimmed rows — the ports come from booking_url.
      setPrefetched(LIST_BUCKET, boatRentalStatsSeedKey(city.id), boatCityStats(inCity));
    }
  }

  // Type pages: the rows are filtered from the same ordered list, so each one
  // reads like its city page with the type filter set (top cards first).
  const typePages = boatTypePages(all);
  setPrefetched(LIST_BUCKET, BOAT_TYPE_PAGES_SEED_KEY, typePages);
  for (const [cityId, types] of Object.entries(typePages)) {
    const inCity = all.filter((boat) => boat.city_id === cityId);
    for (const type of types) {
      const ofType = boatsOfTypePage(inCity, type);
      for (const locale of SUPPORTED_LOCALES) {
        setPrefetched(
          LIST_BUCKET,
          boatTypePageSeedKey(cityId, type, locale),
          ofType.map((boat, index) => trimBoatForRentalPage(boat, index, locale)),
        );
      }
      setPrefetched(LIST_BUCKET, boatTypeStatsSeedKey(cityId, type), boatCityStats(ofType));
    }
  }
}

async function fetchIndexLists(): Promise<void> {
  const [events, tours] = await Promise.all([
    Promise.resolve(supabase.rpc(EVENTS_RPC, eventsRpcArgs(EVENTS_INDEX_QUERY))),
    Promise.resolve(supabase.rpc('tours_by_city', toursRpcArgs(TOURS_INDEX_QUERY))),
    fetchArticlesIndexLists(),
    fetchCityLists(),
    fetchCityPageLists(),
    fetchWineLists(),
    fetchBoatLists(),
  ]);

  // A failed seed must not fail the export — the page still renders and the
  // client fetch fills it in, exactly as before this optimisation existed. But
  // say so loudly, because the symptom (an index page with no links) is
  // otherwise invisible until someone re-audits the crawl graph.
  if (events.error) {
    console.warn(`list-prefetch: events seed failed — ${events.error.message}`);
  } else {
    setPrefetched(
      LIST_BUCKET,
      EVENTS_INDEX_SEED_KEY,
      ((events.data ?? []) as DbEvent[]).map(trimEvent),
    );
  }

  if (tours.error) {
    console.warn(`list-prefetch: tours seed failed — ${tours.error.message}`);
  } else {
    setPrefetched(LIST_BUCKET, TOURS_INDEX_SEED_KEY, ((tours.data ?? []) as Tour[]).map(trimTour));
  }

  // The "tours & boats in this city" seeds: the head of each city's (or the
  // region's) already-trimmed list, so they match what those lists show first.
  for (const cityId of [...cities.map((city) => city.id), null]) {
    const tourRows = getPrefetched<Tour[]>(
      LIST_BUCKET,
      cityId ? cityToursSeedKey(cityId) : TOURS_INDEX_SEED_KEY,
    );
    const boatRows = getPrefetched<Boat[]>(
      LIST_BUCKET,
      cityId ? cityBoatsSeedKey(cityId) : BOATS_INDEX_SEED_KEY,
    );
    if (tourRows) {
      setPrefetched(LIST_BUCKET, crossSellToursSeedKey(cityId), tourRows.slice(0, CROSS_SELL_LIMIT));
    }
    if (boatRows) {
      setPrefetched(LIST_BUCKET, crossSellBoatsSeedKey(cityId), boatRows.slice(0, CROSS_SELL_LIMIT));
    }
  }

  // Read back from the same lists the page generators use, so the footer can
  // only ever link to a /boat-rental, /wine-tours, /[city]/events or
  // /[city]/tours page that exists. A failed seed leaves that list empty — no
  // pages were exported for it either.
  const listingPages = getPrefetched<CityListingPages>(LIST_BUCKET, CITY_LISTING_PAGES_SEED_KEY);
  const footerLinks: FooterCityLinks = {
    boatRental: getPrefetched<string[]>(LIST_BUCKET, BOAT_RENTAL_CITIES_SEED_KEY) ?? [],
    wineTours: getPrefetched<string[]>(LIST_BUCKET, WINE_TOUR_CITIES_SEED_KEY) ?? [],
    cityEvents: listingPages?.events ?? [],
    cityTours: listingPages?.tours ?? [],
  };
  setPrefetched(LIST_BUCKET, FOOTER_LINKS_SEED_KEY, footerLinks);
}

export function prefetchIndexLists(): Promise<void> {
  if (!listsPromise) listsPromise = fetchIndexLists();
  return listsPromise;
}
